import axios from 'axios'
import { logIn, logInWithOkta } from '@src/api/auth'
import { getSavedState, saveState } from '@state/helpers'
import { OktaAuth } from '@okta/okta-auth-js'
import moment from 'moment'

export const state = {
  currentToken: getSavedState('auth.currentToken'),
  adminAuthError: null,
  isRefreshingToken: getSavedState('auth.isRefreshingToken'),
  shouldStartNotificationLoop: true,
  shouldShowReferralExpiredReminderToast: getSavedState('auth.shouldShowReferralExpiredReminderToast', true),
  shouldShowPersonalEmailChangedToast: getSavedState('auth.shouldShowPersonalEmailChangedToast', true),
  shouldShowEducationHighlightAsManager: getSavedState('auth.shouldShowEducationHighlightAsManager', true),
  onlinePresencePendingReviewsCount: getSavedState('auth.onlinePresencePendingReviewsCount', {
    gmb: [],
    facebook: [],
  }),
}

export const mutations = {
  SET_CURRENT_TOKEN(state, newValue) {
    if (newValue) {
      newValue = {
        ...newValue,
        jwtLastUpdated: new Date().getTime(),
      }
    }
    state.currentToken = newValue
    saveState('auth.currentToken', newValue)
    setDefaultAuthHeaders(state)
  },

  SET_IS_REFRESHING_TOKEN(state, newValue) {
    state.isRefreshingToken = newValue
    saveState('auth.isRefreshingToken', newValue)
  },

  SET_ADMIN_AUTH_ERROR(state, newValue) {
    state.adminAuthError = newValue
  },

  SET_SHOULD_START_NOTIFICATION_LOOP(state, newValue) {
    state.shouldStartNotificationLoop = newValue
  },

  SET_SHOW_REFERRAL_EXPIRED_REMINDER_TOAST(state, newValue) {
    state.shouldShowReferralExpiredReminderToast = newValue
    saveState('auth.shouldShowReferralExpiredReminderToast', newValue)
  },

  SET_SHOW_PERSONAL_EMAIL_CHANGED_TOAST(state, newValue) {
    state.shouldShowPersonalEmailChangedToast = newValue
    saveState('auth.shouldShowPersonalEmailChangedToast', newValue)
  },

  SET_SHOW_EDUCATION_HIGHLIGHT_AS_MANAGER(state, newValue) {
    state.shouldShowEducationHighlightAsManager = newValue
    saveState('auth.shouldShowEducationHighlightAsManager', newValue)
  },

  SET_ONLINE_PRESENCE_PENDING_REVIEWS_COUNT(state, newValue) {
    state.onlinePresencePendingReviewsCount = newValue
    saveState('auth.onlinePresencePendingReviewsCount', newValue)
  },
}

export const getters = {
  // Whether the user is currently logged in.
  loggedIn(state) {
    return !!state.currentToken
  },

  adminAuthError(state) {
    return state.adminAuthError
  },

  getToken(state) {
    return state.currentToken
  },

  isRefreshingToken(state) {
    return state.isRefreshingToken
  },

  getLastUpdatedTime(state) {
    return state.currentToken?.jwtLastUpdated
  },

  shouldStartNotificationLoop() {
    return state.shouldStartNotificationLoop
  },

  shouldShowReferralExpiredReminderToast(state) {
    return state.shouldShowReferralExpiredReminderToast
  },

  shouldShowPersonalEmailChangedToast(state) {
    return state.shouldShowPersonalEmailChangedToast
  },

  shouldShowEducationHighlightAsManager(state) {
    return state.shouldShowEducationHighlightAsManager
  },

  onlinePresencePendingReviewsCount(state) {
    return state.onlinePresencePendingReviewsCount
  },
}

export const actions = {
  init({ state, dispatch }) {
    setDefaultAuthHeaders(state)
  },

  // Logs in the current user.
  logIn({ commit, dispatch, getters }, { email, password } = {}) {
    if (getters.loggedIn) {
      return dispatch('validate')
    }

    return logIn({
      email,
      password,
    }).then(async (response) => {
      const user = response.data
      const stateToken = response?.data?.stateToken

      if (stateToken) {
        return user
      }

      commit('SET_CURRENT_TOKEN', user)
      await dispatch('user/getMyUser', null, { root: true })
      return user
    })
  },

  // Logs in the current user with Okta.
  logInWithOkta({ commit, dispatch, getters }, { email, sessionToken } = {}) {
    if (getters.loggedIn) {
      return dispatch('validate')
    }

    return logInWithOkta({
      email,
      sessionToken,
    }).then(async (response) => {
      const user = response.data
      const stateToken = response?.data?.stateToken

      if (stateToken) {
        return user
      }

      commit('SET_CURRENT_TOKEN', user)
      await dispatch('user/getMyUser', null, { root: true })
      return user
    })
  },

  // Logs out the current user.
  logOut({ commit }, path) {
    const okta = new OktaAuth({
      issuer: process.env.OKTA_URL,
    })
    okta.closeSession().then(() => {})
    commit('SET_CURRENT_TOKEN', null)
  },

  // Validates the current user's token and refreshes it
  // with new data from the API.
  validate({ getters, commit, state }) {
    if (!state.currentToken) {
      return Promise.reject(new Error('NO_TOKEN_FOUND'))
    }

    if (isTokenExpired(getters)) {
      if (hoursTokenExpiredBy(getters) < 8) {
        commit('SET_ADMIN_AUTH_ERROR', 'Your session has expired. Please log in again')
      }
      commit('SET_CURRENT_TOKEN', null)
      return null
    }

    if (isTimeForRefresh(getters)) {
      commit('SET_IS_REFRESHING_TOKEN', true)
      return axios
        .get('/user/extend-session')
        .then((response) => {
          const token = response.data
          commit('SET_CURRENT_TOKEN', token)
          return token
        })
        .catch((error) => {
          commit('SET_ADMIN_AUTH_ERROR', 'Your session has expired. Please log in again')
          if (error.response) {
            commit('SET_CURRENT_TOKEN', null)
          }

          return null
        })
        .finally(() => {
          commit('SET_IS_REFRESHING_TOKEN', false)
        })
    }

    return Promise.resolve(state.currentToken)
  },
}

// ===
// Private helpers
// ===

function setDefaultAuthHeaders(state) {
  axios.defaults.headers.common.Authorization = state.currentToken?.token ? `Bearer ${state.currentToken.token}` : ''
}

export const isTimeForRefresh = (getters, lastUpdatedTime = 0) => {
  const jwtRefreshTime = process.env.JWT_REFRESH_TIME_IN_MIN * 60000
  lastUpdatedTime = lastUpdatedTime || getters.getLastUpdatedTime
  return lastUpdatedTime < new Date().getTime() - jwtRefreshTime
}

function isTokenExpired(getters) {
  const jwtExpireTime = process.env.JWT_EXPIRED_TIME_IN_MIN * 60000
  return getters.getLastUpdatedTime < new Date().getTime() - jwtExpireTime
}

function hoursTokenExpiredBy(getters) {
  const jwtExpireTime = process.env.JWT_EXPIRED_TIME_IN_MIN * 60000
  const getLastUpdateDateObject = moment(getters.getLastUpdatedTime)
  const jwtExpireDateObject = moment(moment.now().valueOf() - jwtExpireTime)
  return jwtExpireDateObject.diff(getLastUpdateDateObject, 'hours')
}
